/**
 * Custom Fields — shared contract between API, web app, and clients.
 *
 * A custom field is a tenant-defined attribute that can be attached to one or
 * more object entities (users, customers, engagements). Values are stored inline as a
 * JSONB column on the object row, keyed by the field's UUID. See
 * `docs/custom-fields.md` for the full data model and rationale.
 */

import { z } from 'zod';

export const CUSTOM_FIELD_OBJECTS = ['user', 'customer', 'sow'] as const;
export type CustomFieldObject = (typeof CUSTOM_FIELD_OBJECTS)[number];

export const CUSTOM_FIELD_OBJECT_LABELS: Record<CustomFieldObject, string> = {
  user: 'Users',
  customer: 'Customers',
  sow: 'Engagements',
};

export const CUSTOM_FIELD_TYPES = [
  'text',
  'number',
  'date',
  'checkbox',
  'select',
  'multi_select',
] as const;
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];

export const CUSTOM_FIELD_TYPE_LABELS: Record<CustomFieldType, string> = {
  text: 'Text',
  number: 'Number',
  date: 'Date',
  checkbox: 'Checkbox',
  select: 'Select',
  multi_select: 'Multi-select',
};

export const CUSTOM_FIELD_STATUSES = ['active', 'archived'] as const;
export type CustomFieldStatus = (typeof CUSTOM_FIELD_STATUSES)[number];

/** Per-object configuration on a custom field definition. */
export interface CustomFieldObjectConfig {
  enabled: boolean;
  required: boolean;
  searchable: boolean;
}

/**
 * A field's object_config is a partial map keyed by object. Only enabled objects
 * appear; absence means the field doesn't apply to that object at all.
 */
export type CustomFieldObjectConfigMap = Partial<
  Record<CustomFieldObject, CustomFieldObjectConfig>
>;

/**
 * Options blob — currently only used by select / multi_select to declare the
 * allowed choices. Kept open-ended so additional per-type options can be
 * added without a migration.
 */
export interface CustomFieldOptions {
  choices?: string[];
}

/** Value stored on an object row's `custom_fields` JSONB column. */
export type CustomFieldValue = string | number | boolean | string[] | null;
export type CustomFieldValues = Record<string, CustomFieldValue>;

// ---------------------------------------------------------------------------
// Zod schemas — single source for the API validators, the typed client, and
// the web form. Each of those consumers used to redefine the shape with
// subtle drift (different defaults, partial vs not, etc.); import from here
// instead.
// ---------------------------------------------------------------------------

/** Per-object flags zod schema. Defaults match `CustomFieldObjectConfig`. */
export const CustomFieldObjectConfigSchema = z.object({
  enabled: z.boolean(),
  required: z.boolean().default(false),
  searchable: z.boolean().default(false),
});

/**
 * Per-definition `objectConfig` map. Partial keyed by `CustomFieldObject`;
 * absent keys mean "the field doesn't apply to that object". Use this
 * schema everywhere the API or UI parses a definition payload.
 */
export const CustomFieldObjectConfigMapSchema = z
  .object(
    Object.fromEntries(
      CUSTOM_FIELD_OBJECTS.map((o) => [o, CustomFieldObjectConfigSchema.optional()]),
    ) as Record<CustomFieldObject, z.ZodOptional<typeof CustomFieldObjectConfigSchema>>,
  )
  .partial();

/** Choices blob for `select` / `multi_select` types. */
export const CustomFieldOptionsSchema = z.object({
  choices: z.array(z.string()).optional(),
});

/**
 * Access the per-object flags off a definition without re-deriving the path
 * everywhere. Returns the default `{ enabled: false, ... }` shape when the
 * object isn't in the map so callers don't have to optional-chain.
 */
export function getObjectFlags(
  def: { objectConfig?: CustomFieldObjectConfigMap | null },
  object: CustomFieldObject,
): CustomFieldObjectConfig {
  const cfg = def.objectConfig?.[object];
  return {
    enabled: cfg?.enabled ?? false,
    required: cfg?.required ?? false,
    searchable: cfg?.searchable ?? false,
  };
}
